import { useState, useEffect } from "react";
import { GoogleLogo, MicrosoftLogo } from "./BrandLogos";
import wordmarkLockup from "./sr-wordmark-lockup.png";
import { C, FONT } from "./ui/theme";
import { TextField, PasswordField, ValidationMessage, Spinner, FormKitStyles } from "./ui/FormKit";
import heroPhoto from "./assets/hero-team-success.jpg";

// Styling deliberately duplicated (not imported) from LoginPage.js's
// `authStyles` — that const isn't exported, and this page needs to look
// identical without risking any change to the login page itself.
const authStyles = `
  html, body {
    min-height: 100%;
    min-height: 100dvh;
    background-image: url(${heroPhoto});
    background-size: cover;
    background-position: center 35%;
    background-repeat: no-repeat;
    background-color: ${C.bgAlt};
    background-attachment: fixed;
  }
  @media (max-width: 768px) {
    html, body { background-attachment: scroll; }
  }
  .auth-page {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background-image: url(${heroPhoto});
    background-size: cover;
    background-position: center 35%;
    background-repeat: no-repeat;
    background-attachment: fixed;
  }
  @media (max-width: 768px) {
    .auth-page { background-attachment: scroll; }
  }
  .auth-page-glow {
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6);
    backdrop-filter: blur(14px) saturate(1.3);
    -webkit-backdrop-filter: blur(14px) saturate(1.3);
  }
  .auth-top, .auth-main { position: relative; z-index: 1; }
  .auth-top { padding: 20px 6%; }
  .auth-main { flex: 1; display: flex; align-items: center; justify-content: center; padding: 24px 20px 72px; }
  .auth-card { padding: 40px 36px; }

  .auth-logo-btn { font-family: ${FONT}; background: none; border: none; padding: 0; cursor: pointer; display: inline-flex; align-items: center; gap: 9px; border-radius: 6px; }
  .auth-logo-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 4px; }

  .auth-btn { font-family: ${FONT}; font-weight: 600; font-size: 15px; border: none; border-radius: 12px; cursor: pointer; width: 100%; padding: 13.5px 0; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: background .15s ease, transform .1s ease, opacity .15s ease; }
  .auth-btn-primary { background: ${C.accent}; color: #fff; }
  .auth-btn-primary:hover:not(:disabled) { background: ${C.accentHover}; }
  .auth-btn-primary:active:not(:disabled) { transform: scale(.985); }
  .auth-btn:disabled { opacity: .55; cursor: default; }
  .auth-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }

  .oauth-btn { font-family: ${FONT}; font-weight: 500; font-size: 14.5px; width: 100%; height: 48px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #fff; color: ${C.text}; border: 1px solid ${C.border}; border-radius: 12px; cursor: pointer; transition: background .15s ease, border-color .15s ease, transform .1s ease; }
  .oauth-btn:hover { background: ${C.bgAlt}; border-color: ${C.textFaint}; }
  .oauth-btn:active { transform: scale(.99); }
  .oauth-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }

  .auth-link-btn { font-family: ${FONT}; background: none; border: none; padding: 0; color: ${C.accent}; font-size: 13.5px; font-weight: 600; cursor: pointer; }
  .auth-link-btn:hover { text-decoration: underline; }
  .auth-link-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; border-radius: 4px; }

  .auth-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 380px) { .auth-grid-2 { grid-template-columns: 1fr; } }

  @media (max-width: 480px) {
    .auth-card { padding: 32px 22px; border-radius: 16px !important; }
    .auth-main { padding: 12px 16px 56px; }
  }
`;

// This page lives at /invite/<token> — there's no client-side router in
// this app (see App.js's plain pathname checks for /terms, /privacy,
// etc.), so the token is just the path segment after "/invite/".
function tokenFromPath() {
  const m = window.location.pathname.match(/^\/invite\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

function friendlyError(e) {
  if (e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(e?.message || "")) {
    return "Can't reach the server right now — check your connection and try again.";
  }
  return e?.message || "Something went wrong. Please try again.";
}

export default function AcceptInvitePage() {
  const [token] = useState(tokenFromPath);
  // "loading" | "invalid" | "ready" | "submitting"
  const [status, setStatus] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    fetch("/api/invite/" + encodeURIComponent(token))
      .then(r => r.ok ? r.json() : Promise.reject(new Error("This invite link isn't valid or has expired.")))
      .then(d => { setInvite(d); setStatus("ready"); })
      .catch(e => { setErrorMsg(friendlyError(e)); setStatus("invalid"); });
  }, [token]);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName) e.firstName = "Required";
    if (!form.lastName) e.lastName = "Required";
    if (!form.password || form.password.length < 8) e.password = "Minimum 8 characters";
    else if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const doAccept = async () => {
    if (!validate()) return;
    setStatus("submitting"); setErrorMsg("");
    try {
      const r = await fetch("/api/invite/" + encodeURIComponent(token) + "/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: form.firstName, lastName: form.lastName, password: form.password }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || "Couldn't accept this invite. Please try again.");
      // Route through the same "/?login_token=...&via_invite=1" redirect the
      // OAuth invite flow (Google/Microsoft buttons above) already uses —
      // App.js's existing OAuth-redirect handling picks this up, logs the
      // user in, and (via via_invite) skips plan selection since they're
      // joining an already-provisioned workspace, not starting a new one.
      window.location.href = "/?login_token=" + encodeURIComponent(d.token) + "&via_invite=1";
    } catch (e) {
      setErrorMsg(friendlyError(e));
      setStatus("ready");
    }
  };

  return (
    <div className="auth-page" style={{ fontFamily: FONT, color: C.text, WebkitFontSmoothing: "antialiased" }}>
      <style>{`*{box-sizing:border-box} ${authStyles}`}</style>
      <FormKitStyles />
      <div className="auth-page-glow" />

      <div className="auth-top">
        <button type="button" className="auth-logo-btn" onClick={() => { window.location.href = "/"; }} aria-label="SDR ReachOut home">
          <img src={wordmarkLockup} alt="SDR ReachOut" style={{ height: 48, width: "auto", display: "block" }} />
        </button>
      </div>

      <div className="auth-main">
        <div style={{ width: "100%", maxWidth: 408 }}>
          <p style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-.2px", textAlign: "center", margin: "0 0 16px", background: "linear-gradient(135deg,#0084FF,#6A28EF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>SDR ReachOut Platform</p>
          <div className="auth-card" style={{ background: "#fff", border: `1px solid ${C.borderSoft}`, borderRadius: 20, boxShadow: "0 1px 2px rgba(0,0,0,.03), 0 10px 28px rgba(0,0,0,.05)" }}>

            {status === "loading" && (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <Spinner size={22} color={C.accent} />
                <p style={{ fontSize: 14, color: C.textSub, marginTop: 14 }}>Checking your invite…</p>
              </div>
            )}

            {status === "invalid" && (
              <>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-.4px", marginBottom: 10, textAlign: "center" }}>Invite not found</h1>
                <p style={{ fontSize: 14, color: C.textSub, marginBottom: 24, textAlign: "center", lineHeight: 1.5 }}>
                  {errorMsg || "This invite link isn't valid or has expired. Ask whoever invited you to send a new one."}
                </p>
                <button type="button" className="auth-btn auth-btn-primary" onClick={() => { window.location.href = "/"; }}>
                  Go to sign in
                </button>
              </>
            )}

            {(status === "ready" || status === "submitting") && invite && (
              <>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-.5px", marginBottom: 6, textAlign: "center" }}>
                  Join {invite.orgName || "the team"}
                </h1>
                <p style={{ fontSize: 14.5, color: C.textSub, marginBottom: 28, textAlign: "center", lineHeight: 1.5 }}>
                  {invite.invitedByName ? `${invite.invitedByName} invited ` : "You've been invited "}
                  <strong style={{ color: C.text }}>{invite.email}</strong> to their SDR ReachOut workspace.
                </p>

                {errorMsg && (
                  <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 20, fontSize: 13.5, color: C.red, lineHeight: 1.5 }}>
                    <span aria-hidden>⚠</span><span>{errorMsg}</span>
                  </div>
                )}

                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    <button type="button" className="oauth-btn" onClick={() => { window.location.href = "/api/auth/oauth/google/start?invite=" + encodeURIComponent(token); }}>
                      <GoogleLogo size={18} /><span>Continue with Google</span>
                    </button>
                    <button type="button" className="oauth-btn" onClick={() => { window.location.href = "/api/auth/oauth/microsoft/start?invite=" + encodeURIComponent(token); }}>
                      <MicrosoftLogo size={18} /><span>Continue with Microsoft</span>
                    </button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 22 }}>
                    <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
                    <span style={{ fontSize: 11.5, color: C.textFaint, fontWeight: 600, letterSpacing: ".3px" }}>OR SET A PASSWORD</span>
                    <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div className="auth-grid-2">
                    <TextField id="invite-firstName" label="First Name" value={form.firstName} onChange={f("firstName")} placeholder="John" error={errors.firstName} autoComplete="given-name" />
                    <TextField id="invite-lastName" label="Last Name" value={form.lastName} onChange={f("lastName")} placeholder="Smith" error={errors.lastName} autoComplete="family-name" />
                  </div>
                  <PasswordField id="invite-password" label="Password" value={form.password} onChange={f("password")} placeholder="Minimum 8 characters" error={errors.password} autoComplete="new-password" />
                  <PasswordField id="invite-confirmPassword" label="Confirm Password" value={form.confirmPassword} onChange={f("confirmPassword")} placeholder="Repeat password" error={errors.confirmPassword} autoComplete="new-password" />
                </div>

                <button type="button" className="auth-btn auth-btn-primary" disabled={status === "submitting"} aria-busy={status === "submitting" || undefined} onClick={doAccept} style={{ marginTop: 22 }}>
                  {status === "submitting" && <Spinner size={15} color="#fff" />}
                  {status === "submitting" ? "Joining…" : `Join ${invite.orgName || "workspace"}`}
                </button>
                <ValidationMessage>{null}</ValidationMessage>
              </>
            )}

          </div>
          <p style={{ textAlign: "center", fontSize: 12, color: C.green, fontWeight: 700, marginTop: 24 }}>© 2026 SDR ReachOut. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
